<template>
    <div v-show="fls.shouldShowControl" class="layers-control">
        <div class="dropdown" ref="dropdownRef">
            <button @click="toggleLayersDropdown"
                class="button-map-color"
                :class="{visible:isVisible}">
                <span>
                    <i class="fa-solid fa-eye mr-3"></i>
                    <i class="fa-solid fa-palette"></i>
                </span>
            </button>
            <div v-if="isOpen" class="color-selection">
                <!-- VIEW normal: single list with current behavior -->
                <template v-if="isViewMode()">
                    <div v-for="layer in fls.layers" :key="layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker[layer.id] && layer.visible,
                        'color-selection-item-disabled': isDisabled(layer.id)}">
                        <div class="item-row" >
                            <label class="item-label" :class="{'item-disabled': isDisabled(layer.id)}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="fls.toggleLayerVisibility(layer)" class="checkbox-input"
                                        :disabled="isDisabled(layer.id)"
                                        :class="{ 'checkbox-disabled': isDisabled(layer.id)}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': isDisabled(layer.id)}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer)" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker[layer.id]"
                                    @update:modelValue="handleColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>  
                            <Button v-if="layer.id === 'segments' && layer.visible" 
                                @click="ms.shuffleSegmentColors" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>
                </template>

                <!-- FIELDS-CONNECTION SINGLE MODE: Single field with hasData -->
                <template v-else-if="isSingleFieldMode()">
                    <div class="layers-group-title">Field 1</div>
                    <div v-for="layer in getSingleFieldLayers()" :key="'single-' + layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker['single-' + layer.id] && layer.visible,
                        'color-selection-item-disabled': !layer.hasData}">
                        <div class="item-row">
                            <label class="item-label" :class="{'item-disabled': !layer.hasData}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="handleSingleFieldVisibility(layer)" class="checkbox-input"
                                        :disabled="!layer.hasData"
                                        :class="{ 'checkbox-disabled': !layer.hasData}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': !layer.hasData}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer) && layer.hasData" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker['single-' + layer.id]"
                                    @update:modelValue="handleSingleFieldColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>
                            <Button v-if="layer.id === 'segments' && layer.visible && layer.hasData" 
                                @click="shuffleSingleFieldSegments" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>
                </template>

                <!-- FIELDS-CONNECTION DOUBLE MODE: Field 1 + Field 2 -->
                <template v-else-if="isDoubleFieldMode()">
                    <!-- Field 1 -->
                    <div class="layers-group-title">Field 1</div>
                    <div v-for="layer in getField1Layers()" :key="'field1-' + layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker['field1-' + layer.id] && layer.visible,
                        'color-selection-item-disabled': !layer.hasData}">
                        <div class="item-row">
                            <label class="item-label" :class="{'item-disabled': !layer.hasData}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="handleField1Visibility(layer)" class="checkbox-input"
                                        :disabled="!layer.hasData"
                                        :class="{ 'checkbox-disabled': !layer.hasData}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': !layer.hasData}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer) && layer.hasData" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker['field1-' + layer.id]"
                                    @update:modelValue="handleField1ColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>
                            <Button v-if="layer.id === 'segments' && layer.visible && layer.hasData" 
                                @click="shuffleField1Segments" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>

                    <!-- Field 2 -->
                    <div class="layers-group-title mt-3">Field 2</div>
                    <!-- don't show on both Field 1 and Field 2 layers farm_roi layer in case of Field Edit mode -->
                    <!-- it will be shown in the Field 1 layer if has been selected -->
                    <div v-for="layer in getField2Layers().filter(layer => layer.id !== 'farm_roi')" :key="'field2-' + layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker['field2-' + layer.id] && layer.visible,
                        'color-selection-item-disabled': !layer.hasData}">
                        <div class="item-row">
                            <label class="item-label" :class="{'item-disabled': !layer.hasData}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="handleField2Visibility(layer)" class="checkbox-input"
                                        :disabled="!layer.hasData"
                                        :class="{ 'checkbox-disabled': !layer.hasData}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': !layer.hasData}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer) && layer.hasData" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker['field2-' + layer.id]"
                                    @update:modelValue="handleField2ColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>
                            <Button v-if="layer.id === 'segments' && layer.visible && layer.hasData" 
                                @click="shuffleField2Segments" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>
                </template>

                <!-- EDIT FIELD: duplicate the layer list, OLD + NEW -->
                <template v-else-if="isEditFieldMode()">

                    <!-- OLD layers -->
                    <div class="layers-group-title">OLD</div>
                    <div v-for="layer in fls.layers" :key="'old-' + layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker[layer.id] && layer.visible,
                        'color-selection-item-disabled': isDisabled(layer.id)}">
                        <div class="item-row" >
                            <label class="item-label" :class="{'item-disabled': isDisabled(layer.id)}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="fls.toggleLayerVisibility(layer)" class="checkbox-input"
                                        :disabled="isDisabled(layer.id)"
                                        :class="{ 'checkbox-disabled': isDisabled(layer.id)}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': isDisabled(layer.id)}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer)" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker[layer.id]"
                                    @update:modelValue="handleColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>  
                            <Button v-if="layer.id === 'segments' && layer.visible" 
                                @click="ms.shuffleSegmentColors" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>

                    <!-- NEW layers -->
                    <div class="layers-group-title mt-3">NEW</div>
                    <!-- don't show on both NEW and OLD layers farm_roi layer in case of Field Edit mode -->
                    <!-- it will be shown in the NEW layer if is creating a new field -->
                    <div v-for="layer in getNewLayers().filter(layer => layer.id !== 'farm_roi')" :key="'new-' + layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker[layer.id] && layer.visible,
                        'color-selection-item-disabled': isNewLayerDisabled(layer.id)}">
                        <div class="item-row" >
                            <label class="item-label" :class="{'item-disabled': isNewLayerDisabled(layer.id)}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="handleNewLayerVisibility(layer)" class="checkbox-input"
                                        :disabled="isNewLayerDisabled(layer.id)"
                                        :class="{ 'checkbox-disabled': isNewLayerDisabled(layer.id)}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': isNewLayerDisabled(layer.id)}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer) && !isNewLayerDisabled(layer.id)" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker[layer.id]"
                                    @update:modelValue="handleNewLayerColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>  
                            <Button v-if="layer.id === 'segments' && layer.visible && !isNewLayerDisabled(layer.id)" 
                                @click="shuffleNewSegmentColors" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>
                </template>

                <!-- CREATE FIELD: only NEW -->
                <template v-else-if="isCreateFieldMode()">
                    <div class="layers-group-title">NEW</div>
                    <div v-for="layer in getNewLayers()" :key="'new-create-' + layer.id" class="color-selection-item" 
                        :class="{ 'has-picker-open': showColorPicker[layer.id] && layer.visible,
                        'color-selection-item-disabled': isNewLayerDisabled(layer.id)}">
                        <div class="item-row" >
                            <label class="item-label" :class="{'item-disabled': isNewLayerDisabled(layer.id)}">
                                <div class="modern-checkbox">
                                    <input type="checkbox" v-model="layer.visible"
                                        @change="handleNewLayerVisibility(layer)" class="checkbox-input"
                                        :disabled="isNewLayerDisabled(layer.id)"
                                        :class="{ 'checkbox-disabled': isNewLayerDisabled(layer.id)}">
                                    <span class="checkbox-custom">
                                        <svg class="checkbox-mark" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                        </svg>
                                    </span>
                                </div>
                                <span class="layer-name" :class="{'layer-disabled': isNewLayerDisabled(layer.id)}">
                                    {{ $t(layer.label) }}
                                </span>
                            </label>
                            <div v-if="layer.visible && !shouldHideColorPicker(layer) && !isNewLayerDisabled(layer.id)" class="flex justify-content-center custom-resize">
                                <ColorPicker class="custom-color-input"
                                    v-model="layer.color"
                                    :overlayVisible="showColorPicker[layer.id]"
                                    @update:modelValue="handleNewLayerColorChange(layer, $event)"
                                    @show="showColorPicker.status = true"
                                    @hide="showColorPicker.status = false"
                                    :style="{'--hover-color':layer.color}"
                                    :appendTo="isFullscreen ? getAppendTopLayerElement() : undefined" />
                            </div>  
                            <Button v-if="layer.id === 'segments' && layer.visible && !isNewLayerDisabled(layer.id)" 
                                @click="shuffleNewSegmentColors" 
                                icon="pi pi-refresh"
                                severity="info"
                                size="small"
                                class="segment-refresh-btn"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>

import { useTasksStore } from '@/stores/tasks'
import { useFieldLayersStore } from '@/stores/fieldLayers'
import { useMaps } from '@/stores/maps'
import { useDrawingStore } from '@/stores/drawing'
import { useSegmentsStore } from '@/stores/segments'
import { useFieldsConnectionStore } from '@/stores/fieldsConnection'
import { useRoute, useRouter } from 'vue-router'
import { useCustomers } from '@/stores/customers'

export default {
    name: 'FieldLayersControl',
    data() {
        return {
            showColorPicker: {
                status: false
            },
            unregisterRouteGuard: null,
            isVisible: false,
            isOpen: false,
            isFullscreen: false,
            listener: null,
            outsideClickListener: null,
            // IDs of ROI layers that should not be shown in NEW
            roiLayerIds: ['roi_entrypoints', 'roi_obstacles', 'roi_logistics', 'cutout']
        }
    },
    setup() {
        const ts = useTasksStore()
        const ms = useMaps()
        const fls = useFieldLayersStore()
        const ds = useDrawingStore()
        const ss = useSegmentsStore()
        const fcs = useFieldsConnectionStore()
        const route = useRoute()
        const router = useRouter()
        const customers = useCustomers()

        return {
            ts,
            ms,
            fls,
            ds,
            ss,
            fcs,
            route,
            router,
            customers
        }
    },
    async mounted() {
        // check the route for visibility
        await this.fls.checkRouteForVisibility()
        // add a route guard to check the route for visibility
        this.unregisterRouteGuard = this.router.afterEach(async (to, from) => {
            // Only check if route name changed, not just params (lat/long/zoom)
            if (to.name !== from.name) {
                await this.fls.checkRouteForVisibility()
            }
        })
        // Add event listener for outside clicks to close the dropdown and check fullscreen change
        this.addListener()
    },
    beforeUnmount(){
        this.removeListener()
    },
    methods: {
        isDrawingRoute() {
            return this.route?.name === 'drawing' || this.route?.name === 'drawing-customer'
        },
        isFieldsConnectionRoute() {
            return this.route?.name === 'fields-connection' || this.route?.name === 'fields-connection-customer'
        },
        hasFieldIdInRoute() {
            const params = this.route?.params || {}
            return Boolean(params.id || params.fieldId)
        },
        isEditFieldMode() {
            return this.isDrawingRoute() && !!this.ds.field.id
        },
        isCreateFieldMode() {
            return this.isDrawingRoute() && !this.ds.field.id
        },
        isViewMode() {
            return !this.isDrawingRoute() && !this.isFieldsConnectionRoute() && this.hasFieldIdInRoute()
        },
        isDoubleFieldMode() {
            return this.isFieldsConnectionRoute() && this.fcs.connectionMode === 'double'
        },
        isSingleFieldMode() {
            return this.isFieldsConnectionRoute() && this.fcs.connectionMode === 'single'
        },
        getNewLayers() {
            if (!this.fls.newLayers) return []
            return this.fls.newLayers.filter(layer => !this.roiLayerIds.includes(layer.id))
        },
        getSingleFieldLayers() {
            return this.fls.singleFieldLayers || []
        },
        getField1Layers() {
            return this.fls.field1Layers || []
        },
        getField2Layers() {
            return this.fls.field2Layers || []
        },
        addListener() {
            if (!this.listener && this.fls.shouldShowControl) {
                this.listener = this.handleFullscreenChange.bind(this)
                document.addEventListener('fullscreenchange', this.listener)
            }
        },
        addOutsideClickListener() {
            if (!this.outsideClickListener) {
                this.outsideClickListener = this.handleOutsideClick.bind(this)
                document.addEventListener('click', this.outsideClickListener)
            }
        },
        handleFullscreenChange() {
            this.isFullscreen = !!document.fullscreenElement
        },
        getAppendTopLayerElement() {
            let topLayerElement = document.querySelector('.gm-style')
            if (document.fullscreenElement) {
                topLayerElement = document.fullscreenElement.querySelector('.gm-style')
            }
            return topLayerElement
        },
        // Handle focus event for the color picker
        handleColorChange(layer, newColor) {
            newColor = `#${newColor}`
            this.fls.updateLayerColor(layer, newColor)
        },
        // For NEW layers in edit/create mode
        handleNewLayerColorChange(layer, newColor) {
            newColor = `#${newColor}`
            this.fls.updateNewLayerColor(layer, newColor)
        },
        handleNewLayerVisibility(layer) {
            this.fls.toggleNewLayerVisibility(layer)
        },
        // manage toggleDropdown
        toggleLayersDropdown() {
            this.isOpen = !this.isOpen
            this.isVisible = !this.isVisible
            
            // Add outside click listener when opening
            if (this.isOpen) {
                this.addOutsideClickListener()
            }
            
            // When opening the panel in drawing mode, update the NEW layers
            if (this.isOpen && (this.isEditFieldMode() || this.isCreateFieldMode())) {
                this.fls.initializeNewLayers()
            }
            
            // When opening the panel in fields-connection single mode
            // Always reinitialize to ensure layers are in sync with map data
            if (this.isOpen && this.isSingleFieldMode() && this.fcs.startField_id) {
                this.fls.initializeSingleFieldLayers()
                this.fls.syncListRoiSubLayersFromGeneratedRois(this.fls.singleFieldLayers)
            }
            
            // When opening the panel in fields-connection double mode
            // Always reinitialize to ensure layers are in sync with map data
            if (this.isOpen && this.isDoubleFieldMode()) {
                this.fls.initializeField1Layers()
                this.fls.initializeField2Layers()
                this.fls.syncListRoiSubLayersFromGeneratedRois(this.fls.field1Layers)
                this.fls.syncListRoiSubLayersFromGeneratedRois(this.fls.field2Layers)
            }
        },
        // Function to handle outside clicks to close the dropdown
        handleOutsideClick(event) {
            const dropdownRef = this.$refs.dropdownRef
            if (this.isOpen && dropdownRef && !dropdownRef.contains(event.target) && !this.showColorPicker.status) {
                this.isOpen = false
                this.isVisible = false
                this.removeOutsideClickListener()
            }
        },
        removeOutsideClickListener() {
            if (this.outsideClickListener) {
                document.removeEventListener('click', this.outsideClickListener)
                this.outsideClickListener = null
            }
        },
        isDisabled(id) {
            // Check if ROI layer has any ROIs
            if (id === 'roi_entrypoints' || id === 'roi_obstacles' || id === 'roi_logistics' || id === 'cutout') {
                return !this.fls.hasROIsForLayer(id)
            }
            
            return false
        },
        // Check if a NEW layer is disabled (doesn't have data available in ds)
        isNewLayerDisabled(id) {
            switch (id) {
                case 'field':
                case 'outer_border':
                    return !(this.ds.field.shapes && this.ds.field.shapes.length > 0)
                case 'headland':
                    return !(this.ds.headlandPolygons && this.ds.headlandPolygons.length > 0)
                case 'ab_lines':
                    return !(this.ds.abLinesPolylines && this.ds.abLinesPolylines.length > 0)
                case 'segments':
                    return !(this.ss.allSegmentsLines && this.ss.allSegmentsLines.length > 0)
                case 'track_wheels':
                    return !(this.ds.wheelTrackLinesPolylines && this.ds.wheelTrackLinesPolylines.length > 0)
                case 'farm_roi':
                    return !(this.customers.selectedCustomers.length === 1)
                case 'parking_lots':
                    return !(this.customers.selectedCustomers.length === 1)
                default:
                    return false
            }
        },
        removeListener() {
            if (this.listener) {
                document.removeEventListener('fullscreenchange', this.listener)
                this.listener = null
            }
            this.removeOutsideClickListener()
        },
        shouldHideColorPicker(layer) {
            switch (layer.id) {
                case 'roi_entrypoints':
                case 'roi_obstacles':
                case 'roi_logistics':
                case 'cutout':
                case 'farm_roi':
                case 'parking_lots':
                case 'segments':
                    return true
                default:
                    return false
            }
        },
        shuffleSegmentColors() {
            this.ms.shuffleSegmentColors()
        },
        // Shuffle the NEW segments (from drawing mode)
        shuffleNewSegmentColors() {
            this.ss.shuffleColors(true)
        },
        handleSingleFieldVisibility(layer) {
            this.fls.toggleSingleFieldLayerVisibility(layer)
        },
        handleSingleFieldColorChange(layer, newColor) {
            this.fls.updateSingleFieldLayerColor(layer, `#${newColor}`)
        },
        shuffleSingleFieldSegments() {
            // single field mode in field-connection
            this.fls.shuffleSingleFieldSegmentColors()
        },
        handleField1Visibility(layer) {
            // field 1 mode in field-connection double mode
            this.fls.toggleFieldLayerVisibility(layer, 0)
        },
        handleField2Visibility(layer) {
            // field 2 mode in field-connection double mode
            this.fls.toggleFieldLayerVisibility(layer, 1)
        },
        handleField1ColorChange(layer, newColor) {
            this.fls.updateFieldLayerColor(layer, `#${newColor}`, 0)
        },
        handleField2ColorChange(layer, newColor) {
            this.fls.updateFieldLayerColor(layer, `#${newColor}`, 1)
        },
        shuffleField1Segments() {
            // shuffle field 1 segments in field-connection double mode
            this.fls.shuffleFieldSegmentColors(0)
        },
        shuffleField2Segments() {
            // shuffle field 2 segments in field-connection double mode
            this.fls.shuffleFieldSegmentColors(1)
        }
    }
}
</script>

<style src="@/views/field/FieldLayersControl.scss" lang="css" ></style>
